import React, { useEffect, useMemo, useRef } from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

/**
 * Chaves de skills aceitas pelo backend: receptionist, faq, ticket_seller.
 * Ordem usada como order_index ao enviar.
 */
export const SKILL_OPTION_KEYS = ["receptionist", "faq", "ticket_seller"];

/** @deprecated use SKILL_OPTION_KEYS */
export const SKILL_OPTIONS = [
  { skill_key: "receptionist", label: "Recepcionista", desc: "Atende e direciona o visitante" },
  { skill_key: "faq", label: "Perguntas frequentes", desc: "Responde dúvidas comuns" },
  { skill_key: "ticket_seller", label: "Venda de passagens (BPe)", desc: "Fluxo de passagem e BPe" },
];

/**
 * value: array com exatamente um item `{ skill_key, config?, order_index? }` (formato backend)
 * onChange: (array) => void
 * showTicketSellerSkill: quando false, omite a capacidade de venda de passagens (BPe), exclusiva do segmento transporte rodoviário.
 * includeRoadPassengerCopy: quando true, textos da recepcionista mencionam passagem/BPe; quando false, usam variantes só FAQ/humano (segmento já carregado e não é transporte rodoviário de passageiros).
 */
const SkillSelector = ({
  value = [],
  onChange,
  disabled = false,
  showTicketSellerSkill = true,
  includeRoadPassengerCopy = true,
}) => {
  const { t } = useTranslation("settings");
  const configBySkillRef = useRef({});
  const current = Array.isArray(value) ? value : [];
  const primary = current[0];
  const selectedKey =
    typeof primary === "object" && primary?.skill_key
      ? primary.skill_key
      : typeof primary === "string"
        ? primary
        : null;

  useEffect(() => {
    const row = Array.isArray(value) ? value[0] : null;
    if (row && typeof row === "object" && row.skill_key) {
      const c = row.config && typeof row.config === "object" && !Array.isArray(row.config) ? row.config : {};
      configBySkillRef.current[row.skill_key] = { ...c };
    }
  }, [value]);

  const visibleSkillKeys = useMemo(
    () => (showTicketSellerSkill ? SKILL_OPTION_KEYS : SKILL_OPTION_KEYS.filter((k) => k !== "ticket_seller")),
    [showTicketSellerSkill]
  );

  const defs = visibleSkillKeys.map((skill_key) => ({
    skill_key,
    label:
      skill_key === "receptionist"
        ? t("agentsListPage.skillReceptionist")
        : skill_key === "faq"
          ? t("agentsListPage.skillFaq")
          : t("agentsListPage.skillTicketSeller"),
    desc:
      skill_key === "receptionist"
        ? t(
            includeRoadPassengerCopy
              ? "agentsListPage.skillReceptionistDesc"
              : "agentsListPage.skillReceptionistDescGeneric"
          )
        : skill_key === "faq"
          ? t("agentsListPage.skillFaqDesc")
          : t("agentsListPage.skillTicketSellerDesc"),
  }));

  const selectSkill = (skill_key) => {
    if (disabled) return;
    if (selectedKey === skill_key) return;
    const order_index = Math.max(0, SKILL_OPTION_KEYS.indexOf(skill_key));
    const cached = configBySkillRef.current[skill_key];
    const config = cached && typeof cached === "object" && !Array.isArray(cached) ? { ...cached } : {};
    onChange?.([{ skill_key, config, order_index }]);
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {t("agentsListPage.skillSelectorSingleHint")}
      </Typography>
      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
        {defs.map(({ skill_key, label, desc }) => {
          const selected = selectedKey === skill_key;
          return (
            <Chip
              key={skill_key}
              label={label}
              onClick={() => selectSkill(skill_key)}
              color={selected ? "primary" : "default"}
              variant={selected ? "filled" : "outlined"}
              disabled={disabled}
              sx={{ fontWeight: 500 }}
              title={desc}
            />
          );
        })}
      </Stack>
    </Box>
  );
};

export default SkillSelector;
